import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { breakpointValue, ifThen, otherwise } from '@cpangular/rxjs';
import { EnhancedQueryCondition } from '@idealsupply/query-parser-ts';
import { LabelFrameComponent, LoadMoreComponent, SearchTermBarComponent } from 'common-ui';
import { ErrorComponent } from 'error-ui';
import { OneColumnLayoutComponent } from 'page-layouts-ui';
import { PRODUCT_SEARCH_COMPONENT_STORE } from 'products-data';
import { Observable } from 'rxjs';
import { ProductListingLayoutComponent } from '../product-listing-layout';
import { ProductResultListComponent } from '../product-result-list';

@Component({
  selector: 'ideal-product-search-results',
  imports: [
    OneColumnLayoutComponent,
    // ProductResultGridComponent,
    LoadMoreComponent,
    MatButtonToggleModule,
    MatSelectModule,
    MatIconModule,
    ReactiveFormsModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    SearchTermBarComponent,
    LabelFrameComponent,
    ErrorComponent,
    // ProductResultListComponent,
    ProductResultListComponent,
    ProductListingLayoutComponent,
  ],
  standalone: true,
  templateUrl: './product-search-results.component.html',
  styleUrl: './product-search-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.view]': 'view()',
    '[attr.h-size]': 'componentWidth()',
  },
})
export class ProductSearchResultsComponent {
  private readonly _element = inject(ElementRef<HTMLElement>);
  private readonly _searchStore = inject(PRODUCT_SEARCH_COMPONENT_STORE);
  public readonly floatHeader = input(true);
  public readonly loadMoreMode = input<'automatic' | 'manual'>('automatic');
  protected readonly hasQuery = computed(() => this._searchStore.hasQuery());
  protected readonly query = computed(() => this._searchStore.searchQuery()?.query || '');
  protected readonly sort = computed(() => this._searchStore.searchQuery()?.sort || '');
  protected readonly searchResults = this._searchStore.searchResults;
  protected readonly isSearching = this._searchStore.searchResults.loading;
  protected readonly error = this._searchStore.searchResults.error;
  protected readonly hasMore = this._searchStore.searchResults.hasMore;
  protected readonly resultIds = computed(() => this.searchResults().map((result) => result?.id ?? null));
  protected readonly sortOptions = computed(() => [
    { label: 'Description', value: 'description lineCode partNumber', direction: 'arrow_upward_alt' },
    { label: 'Description', value: '!description lineCode partNumber', direction: 'arrow_downward_alt' },
    { label: 'Line Code', value: 'lineCode partNumber description', direction: 'arrow_upward_alt' },
    { label: 'Line Code', value: '!lineCode !partNumber description', direction: 'arrow_downward_alt' },
    { label: 'Part Number', value: 'partNumber description', direction: 'arrow_upward_alt' },
    { label: 'Part Number', value: '!partNumber description', direction: 'arrow_downward_alt' },
  ]);

  protected readonly queryAst = this._searchStore.searchQueryAST;
  protected readonly form = this._searchStore.searchForm;
  protected readonly view = this._searchStore.view;

  protected readonly componentWidth = toSignal(
    breakpointValue(
      this._element.nativeElement,
      ifThen((size) => size.width < 350, 'sm'),
      ifThen((size) => size.width < 800, 'md'),
      ifThen((size) => size.width < 1200, 'lg'),
      otherwise('xl'),
    ) as Observable<'sm' | 'md' | 'lg' | 'xl'>,
    { initialValue: 'sm' },
  );

  protected readonly forceGridView = computed(() => ['sm', 'md'].includes(this.componentWidth()));
  protected readonly effectiveView = computed(() => (this.forceGridView() ? 'grid' : this.view()));

  protected getSelectedSort(sortValue: string) {
    return this.sortOptions().find((option) => option.value === sortValue) || this.sortOptions()[0];
  }

  protected setView(view: string) {
    this._searchStore.setView(view === 'grid' ? 'grid' : 'list');
  }

  protected loadMore() {
    this._searchStore.searchResults.loadMore(25);
  }

  protected onSubmit(searchForm: FormGroupDirective) {
    searchForm.onSubmit(new Event('submit'));
  }
  protected removedCondition(removed: EnhancedQueryCondition, searchForm: FormGroupDirective) {
    this._searchStore.removeCondition(removed);
    this.onSubmit(searchForm);
  }
}
